import { NotificationService } from '@/services/notification.service';
import { NotificationResponse, NotificationType } from '@/types/notification.types';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopoverModule } from 'primeng/popover';
import { Observable } from 'rxjs';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { TranslateModule } from '@ngx-translate/core';
import { Dialog } from 'primeng/dialog';

interface NotificationTypeInfo {
  class: string;
  icon: string;
  typeClass: string;
}

@Component({
  selector: 'sm-notification-hover',
  imports: [CommonModule, PopoverModule, NotificationItemComponent, TranslateModule, Dialog],
  templateUrl: './notification-hover.component.html',
  styleUrl: './notification-hover.component.scss',
})
export class NotificationHoverComponent implements OnInit {
  notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);

  notifications: NotificationResponse[] = [];
  unreadCount$: Observable<number> = this.notificationService.unreadCount$;

  selectedNotification: NotificationResponse | null = null;
  displayDetails = false;

  page = 0;
  size = 10;
  hasMore = true;
  loading = false;

  ngOnInit(): void {
    this.loadNotifications();
    this.notificationService.notificationReceived$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notif => {
        if (notif) {
          this.notifications = [notif, ...this.notifications];
        }
      });
  }

  loadNotifications(reset = false) {
    if (reset) {
      this.page = 0;
      this.hasMore = true;
      this.notifications = [];
    }
    if (this.loading || !this.hasMore) return;

    this.loading = true;
    this.notificationService.getNotifications(this.page, this.size).subscribe({
      next: (res) => {
        const newNotifs = res.content || [];
        if (newNotifs.length < this.size) {
          this.hasMore = false;
        }
        // Filter out duplicates in case a WebSocket message was prepended
        const existingIds = new Set(this.notifications.map(n => n.id));
        const uniqueNew = newNotifs.filter((n: NotificationResponse) => !existingIds.has(n.id));
        this.notifications = [...this.notifications, ...uniqueNew];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      if (!this.loading && this.hasMore) {
        this.page++;
        this.loadNotifications();
      }
    }
  }

  onNotificationClick(notif: NotificationResponse) {
    this.selectedNotification = notif;
    this.displayDetails = true;

    if (!notif.isRead) {
      this.notificationService.markAsRead(notif.id).subscribe(() => {
        notif.isRead = true;
      });
    }
  }

  markAsRead(event: Event, notif: NotificationResponse) {
    event.stopPropagation();
    if (!notif.isRead) {
      this.notificationService.markAsRead(notif.id).subscribe(() => {
        notif.isRead = true;
      });
    }
  }

  markAllAsRead() {
    this.notificationService.markAllAsRead().subscribe(() => {
      this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
    });
  }

  onDelete(id: number) {
    this.notificationService.deleteNotification(id).subscribe(() => {
      this.notifications = this.notifications.filter(n => n.id !== id);
    });
  }

  getClassesByType(type: NotificationType): NotificationTypeInfo {
    return (
      {
        [NotificationType.INFO]: {
          class: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
          icon: 'pi-info-circle',
          typeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
        },
        [NotificationType.WARNING]: {
          class: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300',
          icon: 'pi-exclamation-triangle',
          typeClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
        },
        [NotificationType.ERROR]: {
          class: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300',
          icon: 'pi-times-circle',
          typeClass: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
        },
        [NotificationType.SUCCESS]: {
          class: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
          icon: 'pi-check-circle',
          typeClass: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
        },
        [NotificationType.TASK_ASSIGNED]: {
          class: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
          icon: 'pi-briefcase',
          typeClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
        },
        [NotificationType.STATUS_CHANGED]: {
          class: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-300',
          icon: 'pi-sync',
          typeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
        },
        [NotificationType.DOCUMENT_UPDATED]: {
          class: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-300',
          icon: 'pi-file',
          typeClass: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
        },
      }[type] || {
        class: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
        icon: 'pi-bell',
        typeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      }
    );
  }
}
